/* Simulatore Orale — UI inmersiva con avatar simple + voz local.
   No usa LLMs runtime: lee preguntas + follow-ups del banco estático del backend.
   Voice: Web Speech API (offline en navegadores que lo soporten — zero-cost).
*/
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock, AlertCircle, ChevronRight, Lock, CheckCircle2,
  Volume2, VolumeX, ListChecks, Trophy, Mic, Send, Loader2,
  SkipForward, Sparkles, Target, FileText, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "../lib/api";
import { flashTensionLevel, FLASH_PALETTE } from "../lib/tension";

const FADE_UP = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

const I18N = {
  it: {
    eyebrow: "Modo Esame Reale",
    title: "Simulazione Reale",
    subtitle: "d'Esame di Riconoscimento",
    intro:
      "Due simulatori che ricreano l'esame ministeriale reale. Scegli la prova scritta a moduli oppure la prova orale con voce e pressione del professore. Tutto in italiano, esattamente come l'esame ufficiale.",
    card_scritto_eyebrow: "Modo Scritto",
    card_scritto_title: "Esame Scritto Reale",
    card_scritto_desc:
      "5 moduli da 1 ora · 30 domande per modulo · banca dati ufficiale italiana · certificato PDF al superamento.",
    card_scritto_cta: "Inizia scritto",
    card_orale_eyebrow: "Modo Orale",
    card_orale_title: "Esame Orale Reale",
    card_orale_desc:
      "5 moduli da 30 minuti · il professore ti interrompe e chiede dettagli · voce reale + microfono + feedback IA per chi vuole il livello premium.",
    card_orale_cta: "Inizia orale",
    rules_title: "Regole del simulatore orale",
    rule_1: "5 moduli · Chirurgia, Clinica, Pediatria, Gineco, Med. Legale",
    rule_2: "30 minuti per modulo (Premium) · 15 minuti (Free) · auto-blocco al termine",
    rule_3: "Banco di 75 domande reali (esami 2023 + Aprile 2026)",
    rule_4: "Il professore preme ogni 5 minuti con sotto-domande",
    rule_5: "Free: 1 domanda per modulo · Premium: 5 domande per modulo",
    difficulty_label: "Difficoltà",
    difficulty_easy: "Facile",
    difficulty_medium: "Medio",
    difficulty_hard: "Difficile",
    difficulty_free_hint: "I tentativi gratuiti sono solo in difficoltà Facile.",
    cta_start: "Inizia simulatore orale",
    cta_resume: "Riprendi simulatore in corso",
    cta_loading: "Sto preparando le domande…",
    credits_chip: "Tentativi orali rimanenti",
    paywall_title: "Sblocca l'esperienza completa",
    paywall_body:
      "Hai esaurito i tentativi gratuiti. Scegli il pacchetto AI Coach: voce reale, feedback personalizzato, risposte modello dei docenti.",
    paywall_cta: "Sblocca pacchetto",
    paywall_unavailable: "Disponibile a breve",
    paywall_recommended: "Più scelto",
    voice_on: "Voce ON",
    voice_off: "Voce OFF",
    speaking: "Il professore sta parlando…",
    module_label: "Modulo",
    question_label: "Domanda",
    timer_label: "Tempo modulo",
    flash_label: "30s",
    flash_aria: "Tempo per rispondere a questa domanda",
    warning_title: "Il tempo sta per scadere",
    warning_body: "Il professore ti sta chiedendo di concludere. Meno di 10 minuti.",
    warning_ack: "Ho capito",
    pressure_msg: "Continui. Mi spieghi anche le complicanze.",
    examiner_intro: "Bene, iniziamo. Mi parli di:",
    skip: "Salta",
    answered: "Risposto",
    mastered: "Padroneggiato",
    next_q: "Prossima domanda",
    advance_module: "Chiudi modulo e passa al successivo",
    advance_last: "Chiudi ultimo modulo e consegna",
    summary_title: "Esame orale completato",
    summary_score: "Punteggio totale",
    summary_passed: "Promosso",
    summary_failed: "Non promosso",
    summary_per_slot: "Punteggio per modulo",
    back_hub: "Torna al hub",
    followup_btn: "Sotto-domanda",
    locked_module: "Bloccato",
    note_placeholder: "Note opzionali per te (rivedile dopo)…",
    no_attempts_in_progress: "Nessun simulatore in corso",
  },
  es: {
    eyebrow: "Modo Examen Real",
    title: "Simulación Real",
    subtitle: "del Examen de Reconocimiento",
    intro:
      "Dos simuladores que recrean el examen ministerial real. Elegí la prueba escrita por módulos o la oral con voz y presión del profesor. Todo en italiano, exactamente como el examen oficial.",
    card_scritto_eyebrow: "Modo Escrito",
    card_scritto_title: "Examen Escrito Real",
    card_scritto_desc:
      "5 módulos de 1 hora · 30 preguntas por módulo · base de datos oficial italiana · certificado PDF al aprobar.",
    card_scritto_cta: "Iniciar escrito",
    card_orale_eyebrow: "Modo Oral",
    card_orale_title: "Examen Oral Real",
    card_orale_desc:
      "5 módulos de 30 minutos · el profesor te interrumpe y pide detalles · voz real + micrófono + feedback IA en el nivel premium.",
    card_orale_cta: "Iniciar oral",
    rules_title: "Reglas del simulador oral",
    rule_1: "5 módulos · Cirugía, Clínica, Pediatría, Gineco, Med. Legal",
    rule_2: "30 minutos por módulo (Premium) · 15 minutos (Free) · auto-bloqueo al final",
    rule_3: "Banco de 75 preguntas reales (exámenes 2023 + Abril 2026)",
    rule_4: "El profesor presiona cada 5 minutos con sub-preguntas",
    rule_5: "Free: 1 pregunta por módulo · Premium: 5 preguntas por módulo",
    difficulty_label: "Dificultad",
    difficulty_easy: "Fácil",
    difficulty_medium: "Medio",
    difficulty_hard: "Difícil",
    difficulty_free_hint: "Los intentos gratuitos son solo en dificultad Fácil.",
    cta_start: "Iniciar simulador oral",
    cta_resume: "Reanudar simulador en curso",
    cta_loading: "Estoy preparando las preguntas…",
    credits_chip: "Intentos orales restantes",
    paywall_title: "Desbloqueá la experiencia completa",
    paywall_body: "Agotaste los intentos gratuitos. Elegí el paquete AI Coach: voz real, feedback personalizado, respuestas modelo de los docentes.",
    paywall_cta: "Desbloquear paquete",
    paywall_unavailable: "Disponible pronto",
    paywall_recommended: "Más elegido",
    voice_on: "Voz ON",
    voice_off: "Voz OFF",
    speaking: "El profesor está hablando…",
    module_label: "Módulo",
    question_label: "Pregunta",
    timer_label: "Tiempo del módulo",
    flash_label: "30s",
    flash_aria: "Tiempo para responder esta pregunta",
    warning_title: "El tiempo se está terminando",
    warning_body: "El profesor te está pidiendo que finalices. Menos de 10 minutos.",
    warning_ack: "Entendido",
    pressure_msg: "Continúe. Explíqueme también las complicaciones.",
    examiner_intro: "Bien, empezamos. Hábleme de:",
    skip: "Saltar",
    answered: "Respondido",
    mastered: "Dominado",
    next_q: "Siguiente pregunta",
    advance_module: "Cerrar módulo y pasar al siguiente",
    advance_last: "Cerrar último módulo y entregar",
    summary_title: "Examen oral completado",
    summary_score: "Puntaje total",
    summary_passed: "Aprobado",
    summary_failed: "No aprobado",
    summary_per_slot: "Puntaje por módulo",
    back_hub: "Volver al hub",
    followup_btn: "Sub-pregunta",
    locked_module: "Bloqueado",
    note_placeholder: "Notas opcionales para vos (revisalas después)…",
    no_attempts_in_progress: "No hay simulador en curso",
  },
};

function formatHMS(secs) {
  const s = Math.max(0, Math.round(secs || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

// ============ AVATAR ============
function ExaminerAvatar({ talking, tone }) {
  // Lightweight SVG. `tone` shifts background color: neutral / pressing / final.
  const palette = {
    neutral: { from: "#5A3FC0", to: "#3D2A8F", ring: "rgba(90,63,192,0.35)" },
    pressing: { from: "#9E2A48", to: "#6B1A30", ring: "rgba(158,42,72,0.5)" },
    final: { from: "#B45309", to: "#7A3F00", ring: "rgba(180,83,9,0.4)" },
  }[tone || "neutral"];
  return (
    <div className="relative inline-block" data-testid="oral-avatar">
      <div className={`w-32 h-32 sm:w-36 sm:h-36 rounded-full overflow-hidden ring-4 transition-all duration-300 ${talking ? "scale-105" : "scale-100"}`}
           style={{ ringColor: palette.ring, boxShadow: `0 12px 36px ${palette.ring}` }}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <defs>
            <linearGradient id="bg-grad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={palette.from} />
              <stop offset="100%" stopColor={palette.to} />
            </linearGradient>
          </defs>
          <rect width="100" height="100" fill="url(#bg-grad)" />
          {/* Head */}
          <circle cx="50" cy="42" r="22" fill="#F5D4B8" />
          {/* Hair */}
          <path d="M28,40 Q28,18 50,18 Q72,18 72,40 L72,32 Q60,28 50,28 Q40,28 28,32 Z" fill="#2A1810" />
          {/* Eyes */}
          <ellipse cx="42" cy="42" rx="2.5" ry={talking ? "2.8" : "2.2"} fill="#1A1A1A" />
          <ellipse cx="58" cy="42" rx="2.5" ry={talking ? "2.8" : "2.2"} fill="#1A1A1A" />
          {/* Eyebrows — angle by tone */}
          <line x1="37" y1="35" x2="47" y2={tone === "pressing" ? "37" : "34"} stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="53" y1={tone === "pressing" ? "37" : "34"} x2="63" y2="35" stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" />
          {/* Mouth — animated when talking */}
          {talking ? (
            <ellipse cx="50" cy="53" rx="4" ry="3" fill="#7A1A20" />
          ) : (
            <path d="M44,53 Q50,55 56,53" stroke="#7A1A20" strokeWidth="1.8" fill="none" strokeLinecap="round" />
          )}
          {/* Collar / camice */}
          <path d="M28,80 Q28,68 36,64 L50,72 L64,64 Q72,68 72,80 L72,100 L28,100 Z" fill="#FFFFFF" />
          <path d="M42,72 L50,90 L58,72" fill="none" stroke="#5A3FC0" strokeWidth="1.5" />
          {/* Stetoscope hint */}
          <circle cx="46" cy="88" r="2" fill="#5A3FC0" />
        </svg>
      </div>
      {talking && (
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1" data-testid="oral-avatar-speaking">
          {[0, 1, 2].map((i) => (
            <span key={i}
                  className="block w-1.5 h-1.5 rounded-full bg-white animate-pulse"
                  style={{ animationDelay: `${i * 120}ms`, background: palette.from }} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============ FLASH RING · 30s per-question countdown ============
// Sober SVG circular ring · stroke color follows `flashTensionLevel`.
// NO sound, NO emoji, NO bouncing. Pulse appears only in critical/imminent.
// At 0 it stays visible (no auto-skip) — pressure is psychological, not
// punitive: the user can still answer at their own pace but FEELS the
// commission "waiting beat".
function FlashRing({ secondsLeft, total = 30, t }) {
  const safeTotal = Math.max(1, total);
  const safeLeft = Math.max(0, Math.min(safeTotal, secondsLeft));
  const tension = flashTensionLevel(safeLeft);
  const palette = FLASH_PALETTE[tension.tone];

  // Geometry · 36px ring, 3px stroke
  const size = 36;
  const stroke = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const ratio = safeLeft / safeTotal;
  const dashOffset = c * (1 - ratio);

  return (
    <span
      data-testid="oral-flash-timer"
      data-tension={tension.tone}
      role="timer"
      aria-label={t.flash_aria}
      className={[
        "relative inline-flex items-center justify-center shrink-0",
        tension.pulse ? "animate-pulse" : "",
      ].join(" ")}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={palette.trackBg}
          strokeWidth={stroke}
        />
        {/* Progress · rotated -90deg so it starts from 12 o'clock */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={palette.stroke}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.85s linear, stroke 0.4s ease" }}
        />
      </svg>
      <span
        className="absolute inset-0 grid place-items-center text-[10px] font-semibold tabular-nums leading-none"
        style={{ color: palette.chipFg }}
      >
        {safeLeft}
      </span>
    </span>
  );
}

// ============ VOICE (Web Speech API) ============
function speak(text, lang = "it") {
  try {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang === "es" ? "es-ES" : "it-IT";
    u.rate = 0.95;
    u.pitch = 0.92;
    window.speechSynthesis.speak(u);
    return u;
  } catch { return null; }
}
function stopSpeaking() {
  try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
}

// ============ HUB (intro) ============
function HubView({ t, language, onStart, starting, hasInProgress, credits, packs,
                   difficulty, setDifficulty, canPickDifficulty, topicsHint = [] }) {
  const navigate = useNavigate();
  const noCredits = !hasInProgress && credits && credits.total_left === 0;
  const [rulesOpen, setRulesOpen] = useState(false);

  const openPackUrl = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else toast.info(t.paywall_unavailable);
  };
  const handleDifficultyClick = (key, isLocked) => {
    if (!isLocked) { setDifficulty(key); return; }
    // Locked difficulty CTA → open the €79,90 AI Coach 10-turni pack (the
    // headline upsell on the page).
    openPackUrl(packs?.oral_coach_pack_10?.url || packs?.oral_coach_pack_5?.url);
  };

  // The two AI Coach packs are the actual revenue funnel (verified Stripe
  // Payment Links — €49,90 / 5 turni and €79,90 / 10 turni).
  const paywallPacks = [
    packs?.oral_coach_pack_5,
    packs?.oral_coach_pack_10,
  ].filter((p) => p && p.url);

  return (
    <motion.section className="max-w-6xl mx-auto" initial="hidden" animate="show" variants={FADE_UP}
                    data-testid="oral-hub">
      {/* ───────────── Topics hint banner (from lesson cross-link) ───────────── */}
      {topicsHint.length > 0 && (
        <div className="mb-4 rounded-xl px-4 py-3 text-sm border"
             style={{ background: "linear-gradient(135deg, #FFFBEE 0%, #FFF1F4 100%)",
                      borderColor: "rgba(184,134,47,0.4)",
                      color: "#7A1322" }}
             data-testid="oral-hub-topics-hint">
          <strong>🎯 Continuità dalla lezione:</strong>{" "}
          stai per esercitarti sui topic <strong>{topicsHint.join(", ")}</strong>.
          Premi "Inizia orale" per partire.
        </div>
      )}
      {/* ───────────── HERO with Italian tricolor ───────────── */}
      <div className="relative overflow-hidden rounded-3xl mb-6"
           style={{
             background: "linear-gradient(135deg, #110826 0%, #1B1235 55%, #0E0A1F 100%)",
           }}>
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{ backgroundImage:
               "radial-gradient(circle at 12% 18%, rgba(0,140,69,0.55) 0%, transparent 45%),"
             + "radial-gradient(circle at 88% 80%, rgba(205,33,42,0.55) 0%, transparent 45%)" }} />
        <div className="h-1.5 w-full flex" aria-hidden="true">
          <div className="flex-1" style={{ background: "#008C45" }} />
          <div className="flex-1" style={{ background: "#FFFFFF" }} />
          <div className="flex-1" style={{ background: "#CD212A" }} />
        </div>
        <div className="relative px-6 sm:px-10 py-9 sm:py-12 text-white">
          <p className="text-[11px] uppercase tracking-[0.28em] mb-3 inline-flex items-center gap-2"
             style={{ color: "#F4D58D" }}>
            <Mic className="h-3.5 w-3.5" /> {t.eyebrow}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
            {t.title}
          </h1>
          <p className="font-serif text-xl sm:text-2xl italic mt-1"
             style={{ color: "#F4D58D" }}>
            {t.subtitle}
          </p>
          <p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed"
             style={{ color: "rgba(255,255,255,0.82)" }}>
            {t.intro}
          </p>
          {credits && (
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
                 style={{ background: "rgba(255,255,255,0.08)",
                          border: "1px solid rgba(244,213,141,0.3)",
                          color: "rgba(255,255,255,0.92)" }}
                 data-testid="oral-credits-chip">
              <Sparkles className="h-3.5 w-3.5" style={{ color: "#F4D58D" }} />
              {t.credits_chip}: <strong className="ml-1" style={{ color: "#F4D58D" }}>{credits.total_left}</strong>
              <span style={{ color: "rgba(255,255,255,0.6)" }}>
                ({credits.free_left} gratuiti · {credits.paid_left} a pagamento)
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ───────────── 2 SPLIT CARDS ───────────── */}
      <div className="grid md:grid-cols-2 gap-5 mb-6">
        {/* SCRITTO — verde italiano */}
        <SimulatorCard
          color="green"
          eyebrow={t.card_scritto_eyebrow}
          title={t.card_scritto_title}
          icon={FileText}
          desc={t.card_scritto_desc}
          ctaLabel={t.card_scritto_cta}
          onCta={() => navigate("/vip/exam")}
          testId="oral-hub-goto-written"
        />
        {/* ORALE — rosso italiano. When the user has no credits left, the
            CTA does NOT disable (that confused free users into thinking the
            click was broken). Instead it morphs into a paywall CTA that
            opens Stripe directly (also scrolls the paywall block into view
            below the fold). */}
        <SimulatorCard
          color="red"
          eyebrow={t.card_orale_eyebrow}
          title={t.card_orale_title}
          icon={Mic}
          desc={t.card_orale_desc}
          ctaLabel={
            hasInProgress ? t.cta_resume
              : (noCredits ? t.paywall_cta : t.card_orale_cta)
          }
          onCta={
            noCredits
              ? () => {
                  // Open the upsell Stripe page directly (one click conversion)
                  openPackUrl(
                    packs?.oral_coach_pack_10?.url
                      || packs?.oral_coach_pack_5?.url
                  );
                  // And scroll the in-page paywall into view for context
                  const el = document.querySelector('[data-testid="oral-paywall"]');
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              : onStart
          }
          ctaLoading={!noCredits && starting}
          ctaDisabled={!noCredits && starting}
          testId="oral-hub-start-oral"
        />
      </div>

      {/* ───────────── PAYWALL (only when no credits left) ───────────── */}
      {noCredits && paywallPacks.length > 0 && (
        <div className="mb-6 rounded-2xl border overflow-hidden"
             style={{ borderColor: "rgba(158,42,72,0.3)",
                      background: "linear-gradient(135deg, #FCE9EE 0%, #FFF1F4 100%)" }}
             data-testid="oral-paywall">
          <div className="px-5 sm:px-7 py-5 border-b" style={{ borderColor: "rgba(158,42,72,0.15)" }}>
            <h3 className="font-serif text-2xl text-brand-primary">{t.paywall_title}</h3>
            <p className="text-sm text-brand-secondary mt-1">{t.paywall_body}</p>
          </div>
          <div className="px-5 sm:px-7 py-5 grid sm:grid-cols-2 gap-4">
            {paywallPacks.map((p, i) => (
              <PackCard key={i} pack={p} language={language} recommended={p.recommended}
                        recommendedLabel={t.paywall_recommended}
                        ctaLabel={t.paywall_cta}
                        onClick={() => openPackUrl(p.url)}
                        testId={`oral-pack-${i}`} />
            ))}
          </div>
        </div>
      )}

      {/* ───────────── RULES + DIFFICULTY (collapsible) ───────────── */}
      <details className="rounded-2xl border border-brand-border bg-white shadow-premium overflow-hidden"
               open={rulesOpen}
               onToggle={(e) => setRulesOpen(e.currentTarget.open)}
               data-testid="oral-rules">
        <summary className="px-5 sm:px-7 py-4 cursor-pointer flex items-center gap-3 hover:bg-brand-bg/40 transition-colors">
          <span className="h-9 w-9 rounded-full bg-brand-green/15 grid place-items-center">
            <ListChecks className="h-4 w-4 text-brand-green" />
          </span>
          <span className="font-serif text-lg text-brand-primary flex-1">{t.rules_title}</span>
          <ChevronRight className={"h-4 w-4 transition-transform " + (rulesOpen ? "rotate-90" : "")}
                        style={{ color: "#7a6e57" }} />
        </summary>
        <div className="px-5 sm:px-7 pb-6 pt-2">
          <ul className="space-y-2.5">
            {[t.rule_1, t.rule_2, t.rule_3, t.rule_4, t.rule_5].map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-brand-primary">
                <span className="mt-1 shrink-0 h-5 w-5 rounded-full bg-brand-green/15 grid place-items-center">
                  <CheckCircle2 className="h-3 w-3 text-brand-green" />
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ul>

          {!hasInProgress && (
            <div className="mt-6 pt-5 border-t border-brand-border">
              <p className="text-sm font-semibold text-brand-primary mb-3 inline-flex items-center gap-2">
                <Target className="h-4 w-4 text-brand-secondary" /> {t.difficulty_label}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="oral-difficulty">
                {[
                  { key: "easy",   label: t.difficulty_easy,   alwaysAllowed: true  },
                  { key: "medium", label: t.difficulty_medium, alwaysAllowed: false },
                  { key: "hard",   label: t.difficulty_hard,   alwaysAllowed: false },
                ].map((d) => {
                  const locked = !d.alwaysAllowed && !canPickDifficulty;
                  const active = difficulty === d.key;
                  return (
                    <button key={d.key}
                            onClick={() => handleDifficultyClick(d.key, locked)}
                            data-testid={`oral-difficulty-${d.key}`}
                            className={[
                              "px-4 py-2 rounded-full text-sm font-medium border transition-all inline-flex items-center gap-1.5",
                              active
                                ? "bg-brand-primary text-white border-brand-primary"
                                : locked
                                  ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 cursor-pointer"
                                  : "bg-white text-brand-primary border-brand-border hover:border-brand-primary",
                            ].join(" ")}>
                      {locked ? <Lock className="h-3.5 w-3.5" /> : null}
                      {d.label}
                      {locked ? <span className="text-[10px] ml-1 opacity-80">€79,90</span> : null}
                    </button>
                  );
                })}
              </div>
              {!canPickDifficulty && (
                <p className="text-xs text-brand-secondary mt-2">{t.difficulty_free_hint}</p>
              )}
            </div>
          )}
        </div>
      </details>
    </motion.section>
  );
}

// ============ Reusable bits for the hub ============

function SimulatorCard({ color, eyebrow, title, icon: Icon, desc, ctaLabel, onCta,
                        ctaLoading, ctaDisabled, testId }) {
  const palette = color === "green"
    ? { accent: "#008C45", soft: "rgba(0,140,69,0.10)", glow: "rgba(0,140,69,0.45)" }
    : { accent: "#CD212A", soft: "rgba(205,33,42,0.10)", glow: "rgba(205,33,42,0.50)" };
  return (
    <div className="relative overflow-hidden rounded-2xl border bg-white transition-all hover:-translate-y-0.5"
         style={{ borderColor: palette.accent + "55",
                  boxShadow: `0 14px 38px -22px ${palette.glow}` }}
         data-testid={testId + "-card"}>
      <div className="h-1.5 w-full" style={{ background: palette.accent }} />
      <div className="p-6 sm:p-7 flex flex-col gap-4 h-full">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl grid place-items-center shrink-0"
               style={{ background: palette.soft }}>
            <Icon className="h-6 w-6" style={{ color: palette.accent }} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold"
               style={{ color: palette.accent }}>
              {eyebrow}
            </p>
            <h2 className="font-serif text-2xl text-brand-primary leading-tight">{title}</h2>
          </div>
        </div>
        <p className="text-sm text-brand-secondary leading-relaxed flex-1">{desc}</p>
        <button onClick={onCta} disabled={ctaDisabled}
                data-testid={testId}
                className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-semibold transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                style={{
                  background: `linear-gradient(135deg, ${palette.accent} 0%, ${palette.accent}DD 100%)`,
                  boxShadow: `0 8px 22px -10px ${palette.glow}`,
                }}>
          {ctaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {ctaLabel}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function PackCard({ pack, language, recommended, recommendedLabel, ctaLabel, onClick, testId }) {
  const label = language === "es" ? pack.label_es : pack.label_it;
  const subtitle = language === "es" ? pack.subtitle_es : pack.subtitle_it;
  return (
    <button onClick={onClick}
            data-testid={testId}
            className="group relative text-left rounded-2xl border-2 p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            style={{
              borderColor: recommended ? "#B8862F" : "rgba(158,42,72,0.25)",
              background: recommended
                ? "linear-gradient(135deg, #FFFBEE 0%, #FFF6E0 100%)"
                : "white",
            }}>
      {recommended && (
        <span className="absolute -top-2.5 left-4 text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-0.5 rounded-full"
              style={{ background: "linear-gradient(135deg, #B8862F, #D4A24A)",
                       color: "white",
                       boxShadow: "0 4px 12px -4px rgba(184,134,47,0.6)" }}>
          ★ {recommendedLabel}
        </span>
      )}
      <p className="font-serif text-lg text-brand-primary leading-tight">{label}</p>
      {subtitle && (
        <p className="text-xs mt-1" style={{ color: recommended ? "#8C6A1F" : "#7a6e57" }}>
          {subtitle}
        </p>
      )}
      <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold"
           style={{ color: recommended ? "#B8862F" : "#9E1B32" }}>
        {ctaLabel}
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </div>
    </button>
  );
}

// ============ RUNNER (oral exam in progress) ============
function RunnerView({ t, language, attempt, onAttemptUpdate }) {
  const navigate = useNavigate();
  const slots = attempt.slots || [];
  const slotIdx = Math.max(0, Math.min(slots.length - 1, Number(attempt.current_slot_index ?? 0)));
  const slot = slots[slotIdx] || { questions: [] };
  const [qIdx, setQIdx] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [warningShown, setWarningShown] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const advancingRef = useRef(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [talking, setTalking] = useState(false);
  const [followupText, setFollowupText] = useState(null);  // {prompt, tone}
  const [followupLoading, setFollowupLoading] = useState(false);
  const [note, setNote] = useState("");
  const [pressureMsg, setPressureMsg] = useState(null);
  const lastPressureRef = useRef(0);

  const q = slot.questions[qIdx];
  const isLastSlot = slotIdx === slots.length - 1;
  const isLastQuestion = qIdx === slot.questions.length - 1;
  const expiryIso = attempt.current_slot_expires_at || attempt.expires_at;
  const tone = followupText?.tone || (secondsLeft <= 600 ? "pressing" : "neutral");

  // ─── 30s flash countdown per question ───
  // Restarts when qid changes (new question = new 30s).
  // Pauses while examiner is "speaking" (so the user doesn't feel cheated
  // by losing seconds while the avatar is still reading the prompt aloud).
  // Stops at 0 (no auto-action). Resets if the user records a status
  // for the current question (so the next question gets a fresh 30s
  // even before the qIdx state propagates).
  const FLASH_TOTAL = 30;
  const [flashLeft, setFlashLeft] = useState(FLASH_TOTAL);
  useEffect(() => { setFlashLeft(FLASH_TOTAL); }, [q?.qid, attempt.attempt_id]);
  useEffect(() => {
    if (!q || talking) return;            // pause while avatar speaks
    if (flashLeft <= 0) return;
    const id = setTimeout(() => setFlashLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [flashLeft, q?.qid, talking]);

  // Reset per-question state when slot/question changes.
  useEffect(() => { setNote(""); setFollowupText(null); }, [slotIdx, qIdx, attempt.attempt_id]);
  useEffect(() => { setQIdx(0); setWarningShown(false); setWarningDismissed(false); }, [slotIdx, attempt.attempt_id]);

  // Speak the current question on mount / change.
  useEffect(() => {
    if (!q || !voiceEnabled) return;
    const text = `${t.examiner_intro} ${language === "es" ? q.prompt_es : q.prompt_it}`;
    const u = speak(text, language);
    if (u) {
      setTalking(true);
      u.onend = () => setTalking(false);
    }
    return () => stopSpeaking();
  }, [q?.qid, voiceEnabled, language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown.
  useEffect(() => {
    const update = () => {
      if (!expiryIso) { setSecondsLeft(0); return; }
      setSecondsLeft(Math.max(0, Math.round((new Date(expiryIso).getTime() - Date.now()) / 1000)));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expiryIso]);

  // 10-minute warning.
  useEffect(() => {
    if (secondsLeft > 0 && secondsLeft <= 600 && !warningShown) setWarningShown(true);
  }, [secondsLeft, warningShown]);

  // Pressure every 5 minutes — examiner interrupts.
  useEffect(() => {
    if (!attempt.pressure_tick_minutes) return;
    const tickSec = attempt.pressure_tick_minutes * 60;
    const elapsed = (attempt.module_duration_minutes * 60) - secondsLeft;
    const currentTick = Math.floor(elapsed / tickSec);
    if (currentTick > lastPressureRef.current && currentTick > 0) {
      lastPressureRef.current = currentTick;
      setPressureMsg(t.pressure_msg);
      if (voiceEnabled) {
        const u = speak(t.pressure_msg, language);
        if (u) { setTalking(true); u.onend = () => setTalking(false); }
      }
      setTimeout(() => setPressureMsg(null), 6500);
    }
  }, [secondsLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  const doAdvance = async () => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setAdvancing(true);
    stopSpeaking();
    try {
      const r = await apiPost(`/vip/oral/${attempt.attempt_id}/advance-slot`);
      if (r?.attempt) onAttemptUpdate(r.attempt);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setAdvancing(false);
      advancingRef.current = false;
    }
  };

  // Auto-advance at timer 0.
  useEffect(() => {
    if (secondsLeft === 0 && expiryIso && !advancingRef.current &&
        new Date(expiryIso).getTime() <= Date.now()) {
      doAdvance();
    }
  }, [secondsLeft, expiryIso]); // eslint-disable-line react-hooks/exhaustive-deps

  const recordStatus = async (status) => {
    if (!q) return;
    try {
      await apiPost(`/vip/oral/${attempt.attempt_id}/answer`,
                    { qid: q.qid, status, note });
      onAttemptUpdate({
        ...attempt,
        answers: { ...(attempt.answers || {}), [q.qid]: { status, note } },
      });
      // Advance to next question or stay on last
      if (qIdx < slot.questions.length - 1) {
        setTimeout(() => setQIdx(qIdx + 1), 250);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const loadFollowup = async () => {
    if (!q?.followups?.length) return;
    setFollowupLoading(true);
    const used = (attempt._fu_used_local || {})[q.qid] || [];
    const next = q.followups.find((f) => !used.includes(f)) || q.followups[0];
    try {
      const data = await apiGet(`/vip/oral/followups/${next}?lang=${language}`);
      setFollowupText(data);
      onAttemptUpdate({
        ...attempt,
        _fu_used_local: {
          ...(attempt._fu_used_local || {}),
          [q.qid]: [...used, next],
        },
      });
      if (voiceEnabled) {
        const u = speak(data.prompt, language);
        if (u) { setTalking(true); u.onend = () => setTalking(false); }
      }
    } catch { /* ignore */ }
    setFollowupLoading(false);
  };

  const isCritical = secondsLeft <= 600;
  const answered = (attempt.answers || {})[q?.qid];

  return (
    <motion.section className="max-w-5xl mx-auto" initial="hidden" animate="show" variants={FADE_UP}
                    data-testid="oral-runner">
      {/* Sticky header: timer + module strip */}
      <div className="sticky top-0 z-20 py-3 mb-5 -mx-3 sm:-mx-6 px-3 sm:px-6"
           style={{
             background: isCritical ? "rgba(255,235,238,0.92)" : "rgba(251,248,242,0.88)",
             backdropFilter: "blur(18px) saturate(140%)",
             WebkitBackdropFilter: "blur(18px) saturate(140%)",
             borderBottom: isCritical ? "1px solid rgba(158,42,72,0.45)" : "1px solid rgba(229,220,239,0.6)",
           }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs text-brand-secondary uppercase tracking-wide">
            {t.module_label} {slotIdx + 1}/{slots.length} · {t.question_label} {qIdx + 1}/{slot.questions.length}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => { setVoiceEnabled((v) => !v); stopSpeaking(); }}
                    data-testid="oral-voice-toggle"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-brand-border hover:border-brand-primary">
              {voiceEnabled ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
              {voiceEnabled ? t.voice_on : t.voice_off}
            </button>
            <span className={["inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold tabular-nums",
                              isCritical ? "animate-pulse" : ""].join(" ")}
                  style={{ background: isCritical ? "rgba(252,233,238,0.95)" : "rgba(255,248,231,0.85)",
                           color: isCritical ? "#9E2A48" : "#7A4F00" }}
                  data-testid="oral-timer">
              <Clock className="h-4 w-4" /> {t.timer_label}: {formatHMS(secondsLeft)}
            </span>
          </div>
        </div>
      </div>

      {/* 10-min warning */}
      {warningShown && !warningDismissed && secondsLeft > 0 && (
        <div className="mb-4 rounded-2xl border px-4 py-3 flex items-start gap-3"
             style={{ background: "linear-gradient(135deg,#FCE9EE 0%,#FFF1F4 100%)",
                      borderColor: "rgba(158,42,72,0.35)", color: "#7A1A36" }}
             data-testid="oral-time-warning">
          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "#9E2A48" }} />
          <div className="flex-1">
            <p className="font-semibold mb-0.5">{t.warning_title}</p>
            <p className="text-sm leading-relaxed">{t.warning_body}</p>
          </div>
          <button onClick={() => setWarningDismissed(true)}
                  className="text-xs font-semibold underline whitespace-nowrap"
                  style={{ color: "#9E2A48" }}>{t.warning_ack}</button>
        </div>
      )}

      {/* Module strip */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="oral-modules-nav">
        {slots.map((s, i) => {
          const active = i === slotIdx;
          const locked = i < slotIdx;
          return (
            <span key={s.slot_id} className={[
                    "px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1.5",
                    active ? "bg-brand-primary text-white border-brand-primary"
                           : locked ? "bg-gray-100 text-gray-500 border-gray-200"
                                    : "bg-white text-gray-400 border-gray-200",
                  ].join(" ")}
                  data-testid={`oral-slot-tab-${s.slot_id}`}>
              {locked ? <Lock className="h-3 w-3" /> : null}
              {language === "es" ? s.label_es : s.label_it}
            </span>
          );
        })}
      </div>

      {/* Avatar + question */}
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
        <div className="flex flex-col items-center gap-2">
          <ExaminerAvatar talking={talking} tone={tone} />
          {talking && (
            <span className="text-xs text-brand-secondary italic" data-testid="oral-speaking-label">
              {t.speaking}
            </span>
          )}
        </div>

        <div className="rounded-2xl border border-brand-border bg-white shadow-premium p-6 sm:p-8"
             data-testid="oral-question-card">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-xs uppercase tracking-[0.18em] text-brand-secondary">
              {language === "es" ? q?.topic_es : q?.topic_it}
            </p>
            <FlashRing secondsLeft={flashLeft} total={FLASH_TOTAL} t={t} />
          </div>
          <h2 className="font-serif text-xl sm:text-2xl text-brand-primary leading-snug mb-4">
            {t.examiner_intro}{" "}
            <span className="font-medium">{language === "es" ? q?.prompt_es : q?.prompt_it}</span>
          </h2>

          {/* Pressure interruption banner */}
          <AnimatePresence>
            {pressureMsg && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                          className="mb-4 px-4 py-2 rounded-lg text-sm font-medium"
                          style={{ background: "rgba(158,42,72,0.12)", color: "#7A1A36" }}
                          data-testid="oral-pressure-msg">
                ⚡ {pressureMsg}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Follow-up box */}
          {followupText && (
            <div className="mb-4 rounded-xl px-4 py-3 border"
                 style={{ background: "rgba(237,231,255,0.55)", borderColor: "rgba(90,63,192,0.3)" }}
                 data-testid="oral-followup-box">
              <p className="text-xs uppercase tracking-wide text-brand-secondary mb-1">
                {t.followup_btn}
              </p>
              <p className="text-brand-primary">{followupText.prompt}</p>
            </div>
          )}

          <textarea value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder={t.note_placeholder}
                    data-testid="oral-note"
                    className="w-full min-h-[72px] px-3 py-2 rounded-xl border border-brand-border text-sm focus:outline-none focus:border-brand-primary"
                    maxLength={500} />

          {/* Self-assessment buttons */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button onClick={() => recordStatus("skipped")}
                    data-testid="oral-status-skipped"
                    className={[
                      "px-4 py-2.5 rounded-xl border font-medium inline-flex items-center justify-center gap-2 transition-all",
                      answered?.status === "skipped"
                        ? "bg-gray-700 text-white border-gray-700"
                        : "bg-white text-gray-600 border-brand-border hover:border-gray-700",
                    ].join(" ")}>
              <SkipForward className="h-4 w-4" /> {t.skip}
            </button>
            <button onClick={() => recordStatus("answered")}
                    data-testid="oral-status-answered"
                    className={[
                      "px-4 py-2.5 rounded-xl border font-medium inline-flex items-center justify-center gap-2 transition-all",
                      answered?.status === "answered"
                        ? "bg-brand-primary text-white border-brand-primary"
                        : "bg-white text-brand-primary border-brand-border hover:border-brand-primary",
                    ].join(" ")}>
              <CheckCircle2 className="h-4 w-4" /> {t.answered}
            </button>
            <button onClick={() => recordStatus("mastered")}
                    data-testid="oral-status-mastered"
                    className={[
                      "px-4 py-2.5 rounded-xl border font-medium inline-flex items-center justify-center gap-2 transition-all",
                      answered?.status === "mastered"
                        ? "bg-brand-green text-white border-brand-green"
                        : "bg-white text-brand-green border-brand-border hover:border-brand-green",
                    ].join(" ")}>
              <Trophy className="h-4 w-4" /> {t.mastered}
            </button>
          </div>

          {/* Follow-up trigger + next */}
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <button onClick={loadFollowup} disabled={followupLoading || !q?.followups?.length}
                    data-testid="oral-followup-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-brand-border bg-white text-brand-primary hover:border-brand-primary disabled:opacity-40">
              {followupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {t.followup_btn}
            </button>
            {!isLastQuestion ? (
              <button onClick={() => setQIdx(qIdx + 1)}
                      data-testid="oral-next-q-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-primary text-white font-medium hover:bg-brand-green">
                {t.next_q} <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={doAdvance} disabled={advancing}
                      data-testid="oral-advance-btn"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-green text-white font-semibold hover:opacity-90 disabled:opacity-50">
                {advancing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {isLastSlot ? t.advance_last : t.advance_module}
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

// ============ SUMMARY ============
function SummaryView({ t, language, attempt, onBack }) {
  const result = attempt.result || { per_slot: [], total_score: 0, max_score: 0 };
  const pct = result.max_score ? Math.round((result.total_score / result.max_score) * 100) : 0;
  return (
    <motion.section className="max-w-3xl mx-auto" initial="hidden" animate="show" variants={FADE_UP}
                    data-testid="oral-summary">
      <h1 className="font-serif text-4xl text-brand-primary mb-2">{t.summary_title}</h1>
      <div className="mb-6 inline-flex items-center gap-3 px-5 py-3 rounded-2xl"
           style={{ background: attempt.passed
                     ? "linear-gradient(135deg, rgba(16,127,82,0.12) 0%, rgba(16,127,82,0.05) 100%)"
                     : "linear-gradient(135deg, rgba(158,42,72,0.12) 0%, rgba(158,42,72,0.05) 100%)",
                    color: attempt.passed ? "#107F52" : "#9E2A48" }}>
        <Trophy className="h-6 w-6" />
        <div>
          <p className="text-sm uppercase tracking-wide">{t.summary_score}</p>
          <p className="text-3xl font-bold tabular-nums">
            {result.total_score} / {result.max_score} <span className="text-xl opacity-70">({pct}%)</span>
          </p>
          <p className="text-sm font-semibold mt-1">
            {attempt.passed ? t.summary_passed : t.summary_failed}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-brand-border bg-white p-5 mb-6">
        <h2 className="font-serif text-lg text-brand-primary mb-3">{t.summary_per_slot}</h2>
        <div className="space-y-2">
          {result.per_slot.map((s) => {
            const slotPct = s.max ? Math.round((s.score / s.max) * 100) : 0;
            return (
              <div key={s.slot_id} className="flex items-center justify-between text-sm"
                   data-testid={`oral-summary-slot-${s.slot_id}`}>
                <span className="text-brand-primary">{language === "es" ? s.label_es : s.label_it}</span>
                <span className={["font-semibold tabular-nums", s.passed ? "text-brand-green" : "text-red-700"].join(" ")}>
                  {s.score}/{s.max} ({slotPct}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <button onClick={onBack} data-testid="oral-summary-back-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-primary text-white font-semibold hover:bg-brand-green">
        {t.back_hub}
      </button>
    </motion.section>
  );
}

// ============ MAIN PAGE ============
export default function VIPOralExam() {
  const navigate = useNavigate();
  const { attemptId } = useParams();
  // Phase 6.2: the oral simulator ALWAYS runs in Italian — it recreates
  // the real ministerial exam, which is exclusively in Italian. We do
  // NOT honor user.preferred_language here on purpose.
  const language = "it";
  // Phase 8 cross-link: when arrived from a VIP lesson "Pratica all'orale"
  // CTA, the URL carries `?topics=q1,q2&from=lesson`. We surface a small
  // hint banner so the user understands why she landed here. The actual
  // pre-filter is server-side (future enhancement); UI feedback is here.
  const urlParams = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search) : null;
  const topicsParam = urlParams?.get("topics") || "";
  const fromLesson = urlParams?.get("from") === "lesson";
  const topicsHint = fromLesson && topicsParam
    ? topicsParam.split(",").slice(0, 3)
    : [];
  const [credits, setCredits] = useState(null);
  const [packs, setPacks] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [difficulty, setDifficulty] = useState("easy");
  const t = useMemo(() => I18N[language] || I18N.it, []);
  const canPickDifficulty = !!(credits && credits.paid_left > 0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [cr, act] = await Promise.all([
          apiGet("/vip/oral/credits").catch(() => null),
          attemptId
            ? apiGet(`/vip/oral/${attemptId}`).catch(() => null)
            : apiGet("/vip/oral/active").catch(() => ({ attempt: null })),
        ]);
        if (!alive) return;
        setCredits(cr?.credits || null);
        setPacks(cr?.packs || null);
        if ((cr?.credits?.paid_left || 0) > 0) setDifficulty("medium");
        setActive(act?.attempt || null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [attemptId]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const res = await apiPost("/vip/oral/start", { difficulty });
      if (res?.credits) setCredits(res.credits);
      const att = res?.attempt;
      if (att?.attempt_id) {
        setActive(att);
        if (att.attempt_id !== attemptId) navigate(`/vip/oral/${att.attempt_id}`);
      }
    } catch (e) {
      const detail = e?.response?.data;
      if (detail?.detail === "no_credits") {
        setCredits(detail.credits);
        if (detail.packs) setPacks(detail.packs);
      } else {
        toast.error(detail?.detail || "Errore");
      }
    }
    setStarting(false);
  };

  const onBack = () => navigate("/vip/oral");

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-brand-bg">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg pb-12">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 pt-6">
        {active && active.status === "in_progress" ? (
          <RunnerView t={t} language={language} attempt={active} onAttemptUpdate={setActive} />
        ) : active && active.status === "submitted" ? (
          <SummaryView t={t} language={language} attempt={active} onBack={onBack} />
        ) : (
          <HubView t={t} language={language} onStart={handleStart} starting={starting}
                   hasInProgress={!!active} credits={credits} packs={packs}
                   difficulty={difficulty} setDifficulty={setDifficulty}
                   canPickDifficulty={canPickDifficulty}
                   topicsHint={topicsHint} />
        )}
      </div>
    </div>
  );
}
